<template>
    <div class="min-h-full w-full bg-white">

        <!-- Read-only banner. -->
        <div v-if="estimate.locked"
             class="w-full bg-yellow-50 text-center text-sm border-b border-yellow-200 px-2 py-1">
            <i class="fas fa-eye text-yellow-500"></i>
            <span class="text-yellow-600">Read-Only Mode</span>
        </div>

        <div v-if="isLockedForEditing"
             class="w-full bg-yellow-50 text-center text-sm border-b border-yellow-200 px-2 py-2">
            <i class="fas fa-eye text-yellow-500"></i>
            <span class="text-yellow-600">Locked For Editing: Currently Being Edited By {{
                    estimate.current_editor.name
                }}</span>
            <div class="mt-2">
                <ui-button-new :disabled="takingControl" @click.prevent="unlockEstimate()" small warning>
                    Unlock And Take Control
                    <i class="flex-shrink-0 ml-2 fas fa-lock-open"></i>
                </ui-button-new>
            </div>
        </div>

        <div class="mx-auto w-full max-w-screen-2xl pt-8 pb-40 px-8" v-if="estimate.id">
            <div class="flex space-x-8">
                <div class="flex-1">
                    <!-- Estimate Title. -->
                    <input
                        class="text-2xl font-bold text-gray-900 border-transparent rounded shadow-none px-2 py-1 -ml-2.5 mb-0"
                        type="text"
                        v-model="estimate.title"
                        @input="estimate.cannot_be_edited_by_authed_user ? null : $store.save()"
                        :placeholder="`${estimate.terminology} #${estimate.uuid}`"
                        :disabled="estimate.cannot_be_edited_by_authed_user"
                    >

                    <!-- Estimate Metadata. -->
                    <div class="text-sm text-gray-500">
                        <estimates-old-status :estimate="estimate"></estimates-old-status>
                        • Reference {{ estimate.package_reference }} - {{ estimate.id }}
                        <span v-if="estimate.revision">• Revision {{ estimate.revision || 0 }}</span>
                    </div>

                    <div class="sticky top-0 bg-white -mx-2 px-2 mb-8 z-30">
                        <!-- Estimate Tabs. -->
                        <estimates-old-page-tabs
                            class="mt-8"
                            :active-section.sync="activeSection"
                            :active-page.sync="activePage"
                        ></estimates-old-page-tabs>

                        <!-- Estimate section header (if applicable). -->
                        <div v-if="activeSection"
                             class="flex mt-8 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold">
                            <div class="w-6 flex-shrink-0"></div>
                            <div class="w-16 border-l border-r border-gray-200 px-2 py-1">Ref</div>
                            <div class="flex-1 border-r border-gray-200 px-2 py-1">Description</div>
                            <div class="w-20 -ml-px border-r border-gray-200 px-2 py-1">Quantity</div>
                            <div class="w-20 -ml-px border-r border-gray-200 px-2 py-1">Uom</div>
                            <div class="w-28 -ml-px border-r border-gray-200 px-2 py-1">Rate</div>
                            <div class="w-28 -ml-px border-r border-gray-200 px-2 py-1">Total</div>
                            <div class="w-20 -ml-px border-r border-gray-200 px-2 py-1">Specs</div>
                            <div class="w-6 flex-shrink-0 ml-2"></div>
                        </div>
                    </div>

                    <!-- Cover Letter. -->
                    <ui-form-section v-if="isPage('cover')">
                        <ui-input label="To" name="addressed_to" v-model="estimate.addressed_to"
                                  @input="estimate.cannot_be_edited_by_authed_user ? null : $store.save()"
                                  placeholder="Mr Smith..." class="xl:col-span-6"
                                  :disabled="estimate.cannot_be_edited_by_authed_user"></ui-input>
                        <ui-date-picker label="Date" name="date" v-model="estimate.date"
                                        @input="estimate.cannot_be_edited_by_authed_user ? null : $store.save()"
                                        class="xl:col-span-6"
                                        :disabled="estimate.cannot_be_edited_by_authed_user"></ui-date-picker>
                        <ui-textarea autoresize label="Address" name="address_to" v-model="estimate.address_to"
                                     @input="estimate.cannot_be_edited_by_authed_user ? null : $store.save()"
                                     class="xl:col-span-6"
                                     :disabled="estimate.cannot_be_edited_by_authed_user"></ui-textarea>
                        <ui-date-picker label="Valid Until" name="valid_until" v-model="estimate.valid_until"
                                        @input="estimate.cannot_be_edited_by_authed_user ? null : $store.save()"
                                        class="xl:col-span-6"
                                        :disabled="estimate.cannot_be_edited_by_authed_user"></ui-date-picker>
                        <estimates-old-page-cover-letter-fields class="col-span-full"></estimates-old-page-cover-letter-fields>
                    </ui-form-section>

                    <!-- Section. -->
                    <estimates-old-page-section v-else :section="activeSection"></estimates-old-page-section>
                </div>
                <div class="w-72 space-y-8 relative">
                    <div class="w-72 space-y-1 fixed">
                        <estimates-old-panels-actions></estimates-old-panels-actions>
                        <div style="overflow-y: auto; padding-right:12px; margin-top:12px; padding-top:12px; top:0px; height:calc(100vh - 250px)">
                            <estimates-old-panels-collection></estimates-old-panels-collection>
                            <div class="space-y-8 top-8">
                                <estimates-old-panels-local-specs></estimates-old-panels-local-specs>
                                <estimates-old-panels-activity></estimates-old-panels-activity>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {estimateStore} from '@stores'

export default {
    props: {
        id: String,
        admin: Boolean,
    },
    data() {
        return {
            takingControl: false,
            activeSection: null,
            activePage: 'cover',
        }
    },
    computed: {
        isLockedForEditing() {
            return this.estimate.currently_being_edited && (this.estimate.current_editor.id !== appData.user.id)
        },
        isReadOnly() {
            return this.estimate.can_be_edited_by_authed_user;
        },
        $store() {
            return estimateStore
        },
        estimate() {
            return estimateStore.state.estimate
        },
    },
    methods: {
        isPage(page) {
            return this.activeSection === null
                && this.activePage === page
        },
        unlockEstimate() {
            this.takingControl = true
            Http.put(route('api.v1.estimates.take-control', this.estimate.id)).then(function () {
                window.location.reload()
            });
        },
        startBackendEditingNotificationPingInterval() {
            let estimateId = this.id;

            Http.put(route('api.v1.estimates.mark-editing', this.id))

            // Ping the backend every 1 minute to let it know we're still editing.
            window.setInterval(() => {
                Http.put(route('api.v1.estimates.mark-editing', estimateId))
            }, 30000);

        }
    },
    async mounted() {
        $('#app-content').css('padding', 0);
        await estimateStore.init(this.id, this.admin)
        let estimateId = this.id;

        if (this.estimate.can_be_edited_by_authed_user) {
            this.startBackendEditingNotificationPingInterval()
        }

        window.eventBus.$on('estimate-refresh-required', function (payloadEstimateId) {
            let thisEstimateId = parseInt(estimateId, 10);
            if (thisEstimateId === payloadEstimateId) {
                window.location.reload();
            }
        });
    },
}
</script>
